import React, { useState, useEffect, useRef } from 'react'
import { C, F, DEPTS, DEPT_KEYS } from './theme'
import { Card, SectionHead, Btn, Icon, Pill, Avatar } from './prims'
import { useChangeNotice } from '../../hooks/useChangeNotice'
import type { ChangeNotice, CnSignStatus, CnFeedKind, CnAuthorRole, CnSignoffRow, CnFeedRow } from '../../types'

// Change Notice panel (Team & approval tab, mig 077). Raise a formal change
// notice against the forecast — thoughts/concerns, what-changes, affected depts,
// requirements, go-live, blockers — capture the formal executive sign-off, and
// leave role-tagged notes (primary author / update / reply). Syncs to The Big
// Plan as a CMN when the event commits.

const inputStyle: React.CSSProperties = { fontFamily: F.body, fontSize: 13, fontWeight: 600, color: C.ink, background: C.card, border: `1px solid ${C.line}`, borderRadius: 9, padding: '8px 11px', width: '100%', boxSizing: 'border-box' }
const areaStyle: React.CSSProperties = { ...inputStyle, minHeight: 66, resize: 'vertical', lineHeight: 1.45 }
const labelStyle: React.CSSProperties = { fontFamily: F.body, fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: C.inkSoft, marginBottom: 5, display: 'block' }

const STATUS_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  draft: { bg: C.brassBg, fg: C.brassDk, label: 'Draft' },
  submitted: { bg: C.slateSoft, fg: C.slate, label: 'Submitted' },
  approved: { bg: C.greenBg, fg: C.green, label: 'Approved' },
  rejected: { bg: C.oxbloodSoft, fg: C.oxblood, label: 'Rejected' }
}
const SIGN_STYLE: Record<CnSignStatus, { bg: string; fg: string; label: string }> = {
  pending: { bg: C.brassBg, fg: C.brassDk, label: 'Awaiting' },
  approved: { bg: C.greenBg, fg: C.green, label: 'Approved' },
  rejected: { bg: C.oxbloodSoft, fg: C.oxblood, label: 'Rejected' }
}
const KIND_META: Record<CnFeedKind, { label: string; icon: string; fg: string }> = {
  comment: { label: 'Comment', icon: 'msg', fg: C.slate },
  update: { label: 'Update', icon: 'refresh', fg: C.brassDk },
  alert: { label: 'Red alert', icon: 'alert', fg: C.oxblood }
}
const fmtWhen = (iso: string | null) => { if (!iso) return ''; const d = new Date(iso); return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) }

export function ChangeNoticePanel({ eventId, defaultTitle }: { eventId: number; defaultTitle?: string }) {
  const cn = useChangeNotice(eventId)
  const notice = cn.notice
  const raisedRef = useRef(false)

  // Auto-raise the change notice for this idea (one per forecast) with the idea's
  // name as the default title, so the panel always presents open for the team to
  // fill in. Guarded so it fires once. Syncs to The Big Plan as a CMN on commit.
  useEffect(() => {
    if (cn.isLoading || notice || raisedRef.current) return
    const t = (defaultTitle ?? '').trim()
    if (!t) return
    raisedRef.current = true
    cn.save({ title: t, status: 'draft' }).catch(() => { raisedRef.current = false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cn.isLoading, notice, defaultTitle])

  if (cn.isLoading) return null
  if (!notice) {
    return (
      <Card style={{ marginBottom: 16 }}>
        <SectionHead icon="flag" label="Change notice" />
        <div style={{ fontFamily: F.body, fontSize: 13, color: C.inkFaint }}>Preparing the change notice…</div>
      </Card>
    )
  }
  return <NoticeForm eventId={eventId} notice={notice} cn={cn} />
}

function NoticeForm({ eventId: _eventId, notice, cn }: { eventId: number; notice: ChangeNotice; cn: ReturnType<typeof useChangeNotice> }) {
  // Local mirror of the free-text fields — saved on blur so click-away persists.
  const [title, setTitle] = useState(notice.title)
  const [thoughts, setThoughts] = useState(notice.thoughts ?? '')
  const [whatIs, setWhatIs] = useState(notice.whatIs ?? '')
  const [whatChanges, setWhatChanges] = useState(notice.whatChanges ?? '')
  useEffect(() => { setTitle(notice.title); setThoughts(notice.thoughts ?? ''); setWhatIs(notice.whatIs ?? ''); setWhatChanges(notice.whatChanges ?? '') }, [notice.id])

  const saveIf = (changed: boolean, body: Parameters<typeof cn.save>[0]) => { if (changed) cn.save(body).catch(() => {}) }
  const toggleDept = (k: string) => {
    const next = notice.affectedDepts.includes(k) ? notice.affectedDepts.filter((d) => d !== k) : [...notice.affectedDepts, k]
    cn.save({ affectedDepts: next }).catch(() => {})
  }
  const toggleReq = (k: keyof ChangeNotice['reqs']) => cn.save({ reqs: { ...notice.reqs, [k]: !notice.reqs[k] } }).catch(() => {})

  const st = STATUS_STYLE[notice.status] ?? STATUS_STYLE.draft!
  const signTotal = notice.signoff.length
  const signApproved = notice.signoff.filter((s) => s.status === 'approved').length

  return (
    <Card style={{ marginBottom: 16 }}>
      <SectionHead icon="flag" label="Change notice" right={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {notice.bpRef ? <Pill icon="link" fg={C.green} bg={C.greenBg}>{notice.bpRef}</Pill> : <Pill icon="clock" fg={C.inkFaint} bg={C.parchment}>Syncs on commit</Pill>}
          <span style={{ padding: '4px 11px', borderRadius: 999, background: st.bg, color: st.fg, fontFamily: F.body, fontSize: 11.5, fontWeight: 800 }}>{st.label}</span>
        </div>
      } />

      {/* title */}
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Title</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => saveIf(title.trim() !== notice.title && title.trim().length > 0, { title: title.trim() })} style={inputStyle} />
        {notice.createdByName && <div style={{ fontFamily: F.body, fontSize: 11.5, color: C.inkFaint, marginTop: 5 }}>Raised by {notice.createdByName}</div>}
      </div>

      {/* thoughts & concerns */}
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Your thoughts &amp; concerns</label>
        <textarea value={thoughts} onChange={(e) => setThoughts(e.target.value)} onBlur={() => saveIf(thoughts !== (notice.thoughts ?? ''), { thoughts: thoughts || null })} placeholder="What are you weighing up? Risks, dependencies, opinions…" style={areaStyle} />
      </div>

      {/* what is / what changes */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={labelStyle}>What it is</label>
          <textarea value={whatIs} onChange={(e) => setWhatIs(e.target.value)} onBlur={() => saveIf(whatIs !== (notice.whatIs ?? ''), { whatIs: whatIs || null })} style={areaStyle} />
        </div>
        <div>
          <label style={labelStyle}>What will change</label>
          <textarea value={whatChanges} onChange={(e) => setWhatChanges(e.target.value)} onBlur={() => saveIf(whatChanges !== (notice.whatChanges ?? ''), { whatChanges: whatChanges || null })} style={areaStyle} />
        </div>
      </div>

      {/* affected depts */}
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Who is affected</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
          {DEPT_KEYS.map((k) => {
            const on = notice.affectedDepts.includes(k); const d = DEPTS[k]!
            return (
              <button key={k} type="button" onClick={() => toggleDept(k)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 999, cursor: 'pointer', fontFamily: F.body, fontSize: 11, fontWeight: 800, background: on ? d.bg : C.paper, color: on ? d.fg : C.inkFaint, border: `1px solid ${on ? d.fg + '44' : C.lineSoft}` }}>
                <span style={{ width: 6, height: 6, borderRadius: 3, background: on ? d.fg : C.inkFaint }} />{d.short}
              </button>
            )
          })}
        </div>
      </div>

      {/* requirements + go-live */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, marginBottom: 14, alignItems: 'flex-start' }}>
        <div>
          <label style={labelStyle}>Requirements</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {(['documentation', 'process', 'training', 'comms'] as const).map((k) => {
              const on = notice.reqs[k]
              return (
                <button key={k} type="button" onClick={() => toggleReq(k)} style={{ padding: '4px 11px', borderRadius: 999, cursor: 'pointer', fontFamily: F.body, fontSize: 11.5, fontWeight: 800, textTransform: 'capitalize', background: on ? C.greenBg : C.paper, color: on ? C.green : C.inkFaint, border: `1px solid ${on ? C.greenSoft : C.lineSoft}` }}>{k}</button>
              )
            })}
          </div>
        </div>
        <div>
          <label style={labelStyle}>Goes live</label>
          <input type="date" value={notice.goLive ?? ''} onChange={(e) => cn.save({ goLive: e.target.value || null }).catch(() => {})} style={{ ...inputStyle, width: 'auto' }} />
        </div>
      </div>

      {/* blockers */}
      <Blockers list={notice.blockers} onChange={(next) => cn.save({ blockers: next }).catch(() => {})} />

      {/* sign-off */}
      <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${C.lineSoft}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12 }}>
          <span style={{ fontFamily: F.body, fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: C.inkSoft }}>Formal sign-off</span>
          {signTotal > 0 && <span style={{ marginLeft: 'auto', fontFamily: F.body, fontSize: 12, fontWeight: 700, color: signApproved === signTotal ? C.green : C.inkSoft }}>{signApproved} of {signTotal} approved</span>}
        </div>
        <Signoff rows={notice.signoff} onSign={(id, s) => cn.patchSignoff(id, s).catch(() => {})} onRemove={(id) => cn.removeSignoff(id).catch(() => {})} onAdd={(role, name) => cn.addSignoff({ role, name }).catch(() => {})} />
      </div>

      {/* activity / replies */}
      <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${C.lineSoft}` }}>
        <div style={{ fontFamily: F.body, fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: C.inkSoft, marginBottom: 12 }}>Activity &amp; replies</div>
        <FeedComposer onSend={(kind, authorRole, body) => cn.addFeed({ kind, authorRole, body }).catch(() => {})} />
        <FeedList items={notice.feed} />
      </div>

      {/* submit footer */}
      <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
        {notice.status === 'submitted' && <Btn variant="ghost" small icon="edit" onClick={() => cn.save({ status: 'draft' }).catch(() => {})}>Reopen as draft</Btn>}
        {notice.status !== 'submitted' && <Btn variant="primary" small icon="send" onClick={() => cn.save({ status: 'submitted' }).catch(() => {})}>Submit for sign-off</Btn>}
      </div>
    </Card>
  )
}

function Blockers({ list, onChange }: { list: string[]; onChange: (next: string[]) => void }) {
  const [draft, setDraft] = useState('')
  const add = () => { const t = draft.trim(); if (!t) return; onChange([...list, t]); setDraft('') }
  return (
    <div>
      <label style={labelStyle}>Conflicts &amp; blockers</label>
      {list.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
          {list.map((b, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 8, background: C.oxbloodSoft, border: `1px solid ${C.oxblood}22` }}>
              <Icon name="alert" size={13} color={C.oxblood} />
              <span style={{ flex: 1, fontFamily: F.body, fontSize: 12.5, color: C.ink }}>{b}</span>
              <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: C.oxblood, display: 'flex' }}><Icon name="x" size={13} /></button>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add() } }} placeholder="Add a blocker…" style={{ ...inputStyle, flex: 1 }} />
        <Btn variant="secondary" small icon="plus" disabled={!draft.trim()} onClick={add}>Add</Btn>
      </div>
    </div>
  )
}

function Signoff({ rows, onSign, onRemove, onAdd }: { rows: CnSignoffRow[]; onSign: (id: number, s: CnSignStatus) => void; onRemove: (id: number) => void; onAdd: (role: string, name: string | null) => void }) {
  const [role, setRole] = useState('')
  const [name, setName] = useState('')
  const add = () => { const r = role.trim(); if (!r) return; onAdd(r, name.trim() || null); setRole(''); setName('') }
  return (
    <div>
      {rows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
          {rows.map((s) => {
            const sc = SIGN_STYLE[s.status]
            return (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', borderRadius: 10, border: `1px solid ${C.lineSoft}`, background: C.paper }}>
                <Avatar name={s.name ?? s.role} size={32} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontFamily: F.serif, fontSize: 14.5, fontWeight: 700, color: C.ink }}>{s.role}</div>
                  <div style={{ fontFamily: F.body, fontSize: 12, color: C.inkSoft }}>{s.name ?? '—'}{s.signedByName && s.status !== 'pending' ? ` · ${s.status === 'approved' ? 'signed' : 'rejected'} by ${s.signedByName}` : ''}</div>
                </div>
                <span style={{ padding: '4px 10px', borderRadius: 999, background: sc.bg, color: sc.fg, fontFamily: F.body, fontSize: 11, fontWeight: 800 }}>{sc.label}</span>
                {s.status !== 'approved' && <Btn variant="soft" small icon="check" onClick={() => onSign(s.id, 'approved')}>Approve</Btn>}
                {s.status !== 'rejected' && <Btn variant="danger" small icon="x" onClick={() => onSign(s.id, 'rejected')}>Reject</Btn>}
                {s.status !== 'pending' && <button type="button" title="Reset" onClick={() => onSign(s.id, 'pending')} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: C.inkFaint, display: 'flex' }}><Icon name="refresh" size={14} /></button>}
                <button type="button" title="Remove" onClick={() => onRemove(s.id)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: C.inkFaint, display: 'flex' }}><Icon name="x" size={14} /></button>
              </div>
            )
          })}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Role (e.g. Head of Operations)" style={{ ...inputStyle, flex: 1, minWidth: 180 }} />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" style={{ ...inputStyle, flex: 1, minWidth: 140 }} />
        <Btn variant="secondary" small icon="plus" disabled={!role.trim()} onClick={add}>Add signatory</Btn>
      </div>
    </div>
  )
}

const ROLE_OPTS: Array<{ v: CnAuthorRole; label: string }> = [
  { v: 'author', label: 'Primary author' },
  { v: 'update', label: 'Update' },
  { v: 'reply', label: 'Reply' }
]

function FeedComposer({ onSend }: { onSend: (kind: CnFeedKind, role: CnAuthorRole, body: string) => void }) {
  const [role, setRole] = useState<CnAuthorRole>('reply')
  const [kind, setKind] = useState<CnFeedKind>('comment')
  const [body, setBody] = useState('')
  const send = () => { const t = body.trim(); if (!t) return; onSend(kind, role, t); setBody('') }
  const alert = kind === 'alert'
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        <select value={role} onChange={(e) => setRole(e.target.value as CnAuthorRole)} style={{ ...inputStyle, width: 'auto', fontWeight: 700 }}>
          {ROLE_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['comment', 'update', 'alert'] as CnFeedKind[]).map((k) => {
            const on = kind === k; const m = KIND_META[k]
            return (
              <button key={k} type="button" onClick={() => setKind(k)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px', borderRadius: 8, cursor: 'pointer', fontFamily: F.body, fontSize: 12, fontWeight: 800, background: on ? (k === 'alert' ? C.oxbloodSoft : C.greenBg) : C.paper, color: on ? m.fg : C.inkFaint, border: `1px solid ${on ? m.fg + '44' : C.lineSoft}` }}>
                <Icon name={m.icon} size={12} />{m.label}
              </button>
            )
          })}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send() } }} placeholder={alert ? 'Raise a red alert — what is wrong?' : 'Add a note…'} style={{ ...areaStyle, minHeight: 52, borderColor: alert ? C.oxblood + '66' : C.line }} />
        <Btn variant={alert ? 'danger' : 'primary'} small icon="send" disabled={!body.trim()} onClick={send}>Send</Btn>
      </div>
    </div>
  )
}

function FeedList({ items }: { items: CnFeedRow[] }) {
  if (items.length === 0) return <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkFaint }}>No activity yet.</div>
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((f) => {
        const m = KIND_META[f.kind]; const isAlert = f.kind === 'alert'
        return (
          <div key={f.id} style={{ display: 'flex', gap: 10, padding: '9px 11px', borderRadius: 10, background: isAlert ? C.oxbloodSoft : C.paper, border: `1px solid ${isAlert ? C.oxblood + '33' : C.lineSoft}`, borderLeft: `3px solid ${m.fg}` }}>
            <Avatar name={f.authorName ?? '?'} size={30} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: F.body, fontSize: 13, fontWeight: 800, color: C.ink }}>{f.authorName ?? '—'}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: F.body, fontSize: 10, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: m.fg }}><Icon name={m.icon} size={11} />{m.label}</span>
                <span style={{ fontFamily: F.body, fontSize: 10, fontWeight: 700, color: C.inkFaint, textTransform: 'capitalize' }}>· {f.authorRole}</span>
                <span style={{ marginLeft: 'auto', fontFamily: F.mono, fontSize: 10.5, color: C.inkFaint }}>{fmtWhen(f.createdAt)}</span>
              </div>
              <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.ink, marginTop: 3, whiteSpace: 'pre-wrap' }}>{f.body}</div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
